import { forwardRef, type ReactNode, type HTMLAttributes } from "react";
import { cn } from "../../../utils/cn";
import { CloseIcon } from "../Icon";

/* ──────────────────────────────────────────────
 * Dialog（ルートコンテナ）
 * オーバーレイ + 中央配置ラッパー
 * ────────────────────────────────────────────── */

interface DialogProps extends HTMLAttributes<HTMLDivElement> {
    /** 閉じるコールバック（オーバーレイクリック時） */
    onClose?: () => void;
    children: ReactNode;
}

/**
 * Dialog - モーダルダイアログのルートコンテナ
 *
 * @description
 * オーバーレイ＋中央配置を提供する。
 * 内部に DialogHeader / DialogBody / DialogFooter を配置して使う。
 */
const Dialog = forwardRef<HTMLDivElement, DialogProps>(
    ({ onClose, children, className, ...props }, ref) => (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* オーバーレイ */}
            <div
                className="absolute inset-0 bg-black/50"
                onClick={onClose}
                aria-hidden="true"
            />

            {/* モーダル本体 */}
            <div
                ref={ref}
                className={cn(
                    "relative bg-white rounded-lg shadow-xl mx-4",
                    className
                )}
                {...props}
            >
                {children}
            </div>
        </div>
    )
);

Dialog.displayName = "Dialog";

/* ──────────────────────────────────────────────
 * DialogHeader（タイトル + ×ボタン + 下線）
 * ────────────────────────────────────────────── */

interface DialogHeaderProps {
    /** ダイアログのタイトル */
    title: string;
    /** 閉じるコールバック */
    onClose?: () => void;
    /** ヘッダー全体に追加するクラス */
    className?: string;
    children?: ReactNode;
}

/** ダイアログヘッダー（タイトル + ×ボタン + border-b） */
const DialogHeader = ({ title, onClose, className, children }: DialogHeaderProps) => (
    <div className={cn("flex items-center justify-between px-5 py-4 border-b border-gray-200", className)}>
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        <div className="flex items-center gap-2">
            {children}
            {onClose && (
                <button
                    type="button"
                    onClick={onClose}
                    className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="閉じる"
                >
                    <CloseIcon className="w-5 h-5" />
                </button>
            )}
        </div>
    </div>
);

DialogHeader.displayName = "DialogHeader";

/* ──────────────────────────────────────────────
 * DialogBody（コンテンツ領域）
 * ────────────────────────────────────────────── */

interface DialogBodyProps extends HTMLAttributes<HTMLDivElement> {
    children: ReactNode;
}

/** ダイアログの本文エリア */
const DialogBody = forwardRef<HTMLDivElement, DialogBodyProps>(
    ({ children, className, ...props }, ref) => (
        <div
            ref={ref}
            className={cn("px-5 py-4", className)}
            {...props}
        >
            {children}
        </div>
    )
);

DialogBody.displayName = "DialogBody";

/* ──────────────────────────────────────────────
 * DialogFooter（border-t + ボタン群）
 * ────────────────────────────────────────────── */

interface DialogFooterProps extends HTMLAttributes<HTMLDivElement> {
    children: ReactNode;
}

/** ダイアログフッター（border-t + ボタン配置） */
const DialogFooter = forwardRef<HTMLDivElement, DialogFooterProps>(
    ({ children, className, ...props }, ref) => (
        <div
            ref={ref}
            className={cn("flex justify-end gap-2 px-5 py-4 border-t border-gray-200", className)}
            {...props}
        >
            {children}
        </div>
    )
);

DialogFooter.displayName = "DialogFooter";


export { Dialog, DialogHeader, DialogBody, DialogFooter };
export type { DialogProps, DialogHeaderProps, DialogBodyProps, DialogFooterProps };
